import React, { useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { IconButton } from "@mui/material";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { getDir } from "orion-components/i18n/Config/selector";
import { useSelector } from "react-redux";
import AnalogButton from "./AnalogButton";
import { cameraService } from "client-app-core";
import { useEnsurePTZTempLock } from "../../../../SharedComponents/CameraPTZ/ensurePTZTempLock";

const handleCameraNorth = (cameraId, isRelative, instanceId) => {
	if (isRelative) {
		cameraService.moveRelative(cameraId, 0, 1, 1, 1);
	} else {
		cameraService.moveContinuous(cameraId, instanceId, 0, 0.5, 0);
	}
};

const handleCameraWest = (cameraId, isRelative, instanceId) => {
	if (isRelative) {
		cameraService.moveRelative(cameraId, -1, 0, 1, 1);
	} else {
		cameraService.moveContinuous(cameraId, instanceId, -0.5, 0, 0);
	}
};

const handleCameraEast = (cameraId, isRelative, instanceId) => {
	if (isRelative) {
		cameraService.moveRelative(cameraId, 1, 0, 1, 1);
	} else {
		cameraService.moveContinuous(cameraId, instanceId, 0.5, 0, 0);
	}
};

const handleCameraSouth = (cameraId, isRelative, instanceId) => {
	if (isRelative) {
		cameraService.moveRelative(cameraId, 0, -1, 1, 1);
	} else {
		cameraService.moveContinuous(cameraId, instanceId, 0, -0.5, 0);
	}
};

const stopContinuousPanTilt = (cameraId, instanceId) => {
	cameraService.moveContinuous(cameraId, instanceId, 0, 0, 0);
	cameraService.moveContinuous(cameraId, instanceId, 0, 0, 0);
};

const stopContinuousZoom = (cameraId, instanceId) => {
	cameraService.moveContinuous(cameraId, instanceId, 0, 0, 0);
};

const handleZoomIn = (cameraId, isRelative, instanceId) => {
	if (isRelative) {
		cameraService.zoomRelative(cameraId, instanceId, 1, 0.05, 1);
	} else {
		cameraService.moveContinuous(cameraId, instanceId, 0, 0, 0.5);
	}
};
const handleZoomOut = (cameraId, isRelative, instanceId) => {
	if (isRelative) {
		cameraService.zoomRelative(cameraId, instanceId, -1, 0.05, 1);
	} else {
		cameraService.moveContinuous(cameraId, instanceId, 0, 0, -0.5);
	}
};

const PTZController = ({ cameraId, camera, instanceId }) => {
	const dir = useSelector(state => getDir(state));
	const [isAnalogBeingDragged, setIsAnalogBeingDragged] = useState(false);
	const ensureTempLock = useEnsurePTZTempLock(cameraId);

	const styles = useMemo(() => {
		return {
			ptzHeader: {
				position: "absolute",
				bottom: 70,
				...(dir === "ltr" && { left: 15 }),
				...(dir === "rtl" && { right: 15 })
			},
			ptzControllerWrapper: {
				position: "absolute",
				width: 55,
				height: 55
			},
			arrowIcon: {
				color: "#827C7C",
				width: 25,
				height: 25
			},
			arrowUp: {
				position: "absolute",
				top: -4,
				...(dir === "ltr" && { left: "28.5%" }),
				...(dir === "rtl" && { right: "28.5%" }),
				display: "flex",
				width: 25,
				height: 25
			},
			arrowDown: {
				position: "absolute",
				bottom: -7,
				...(dir === "ltr" && { left: "28.5%" }),
				...(dir === "rtl" && { right: "28.5%" }),
				display: "flex",
				width: 25,
				height: 25
			},
			arrowRight: {
				position: "absolute",
				...(dir === "ltr" && { right: -5 }),
				...(dir === "rtl" && { left: -5 }),
				top: "30%",
				display: "flex",
				width: 25,
				height: 25
			},
			arrowLeft: {
				position: "absolute",
				...(dir === "ltr" && { left: -5 }),
				...(dir === "rtl" && { right: -5 }),
				top: "30%",
				display: "flex",
				width: 25,
				height: 25
			},
			zoomButtonsWrapper: {
				position: "absolute",
				bottom: 40,
				...(dir === "ltr" && { left: 15 }),
				...(dir === "rtl" && { right: 15 })
			},
			zoomIn: {
				...(dir === "ltr" && { marginRight: 10 }),
				...(dir === "rtl" && { marginLeft: 10 }),
				display: "flex",
				alignItems: "center",
				justifyContent: "center"
			},
			zoomOut: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center"
			}
		};
	}, [dir]);

	const [isClickHeld, setIsClickHeld] = useState(false);

	const holdTimeoutRef = useRef(null);
	const holdDelay = 500;

	const handleMouseDown = direction => {
		// This function executes when the arrow buttons are held
		holdTimeoutRef.current = setTimeout(() => {
			setIsClickHeld(true);
			changePTZControls(direction, false);
		}, holdDelay);
	};

	const handleMouseUp = direction => {
		clearTimeout(holdTimeoutRef.current);

		if (!isClickHeld) {
			// This block executes the click event triggered by the arrow buttons
			changePTZControls(direction, true);
		} else {
			if (direction === "zoomIn" || direction === "zoomOut") {
				stopContinuousZoom(cameraId, instanceId);
			} else {
				// This block will be called as soon as the mouse holding stops on an arrow key
				stopContinuousPanTilt(cameraId, instanceId);
			}
		}

		setIsClickHeld(false);
	};

	const changePTZControls = (direction, isRelative = false) => {
		if (!isAnalogBeingDragged) {
			// Every PTZ control attempt keeps the temp lock alive (debounced)
			ensureTempLock();

			switch (direction) {
				case "up":
					return handleCameraNorth(cameraId, isRelative, instanceId);
				case "down":
					return handleCameraSouth(cameraId, isRelative, instanceId);
				case "right":
					return handleCameraEast(cameraId, isRelative, instanceId);
				case "left":
					return handleCameraWest(cameraId, isRelative, instanceId);
				case "zoomIn":
					return handleZoomIn(cameraId, isRelative, instanceId);
				case "zoomOut":
					return handleZoomOut(cameraId, isRelative, instanceId);
				default:
					// Handle unexpected direction
					console.error(`Unexpected direction: ${direction}`);
					break;
			}
		}
		return;
	};

	const getDraggingState = value => {
		setIsAnalogBeingDragged(value);
		// When drag starts, refresh temp lock (debounced)
		if (value) {
			ensureTempLock();
		}
	};

	return (
		<div className="ptz-wrapper" dir={dir} style={{ zIndex: 1901 }}>
			<div style={styles.ptzHeader}>
				<div className="ptz-controller-container">
					<div style={styles.ptzControllerWrapper}>
						<div style={styles.arrowUp}>
							<IconButton
								sx={{
									"&.MuiIconButton-root": {
										padding: "0px !important"
									}
								}}
								onMouseDown={() => handleMouseDown("up")}
								onMouseUp={() => handleMouseUp("up")}
							>
								<ArrowDropUpIcon sx={styles.arrowIcon} />
							</IconButton>
						</div>
						<div
							style={styles.arrowDown}
							onMouseDown={() => handleMouseDown("down")}
							onMouseUp={() => handleMouseUp("down")}
						>
							<IconButton
								sx={{
									"&.MuiIconButton-root": {
										padding: "0px !important"
									}
								}}
							>
								<ArrowDropDownIcon sx={styles.arrowIcon} />
							</IconButton>
						</div>
						<div
							style={styles.arrowRight}
							onMouseDown={() => handleMouseDown(dir === "ltr" ? "right" : "left")}
							onMouseUp={() => handleMouseUp(dir === "ltr" ? "right" : "left")}
						>
							<IconButton
								sx={{
									"&.MuiIconButton-root": {
										padding: "0px !important"
									}
								}}
							>
								{dir === "ltr" ? (
									<ArrowRightIcon sx={styles.arrowIcon} />
								) : (
									<ArrowLeftIcon sx={styles.arrowIcon} />
								)}
							</IconButton>
						</div>

						<AnalogButton camera={camera} instanceId={instanceId} getDraggingState={getDraggingState} />

						<div
							style={styles.arrowLeft}
							onMouseDown={() => handleMouseDown(dir === "ltr" ? "left" : "right")}
							onMouseUp={() => handleMouseUp(dir === "ltr" ? "left" : "right")}
						>
							<IconButton
								sx={{
									"&.MuiIconButton-root": {
										padding: "0px !important"
									}
								}}
							>
								{dir === "ltr" ? (
									<ArrowLeftIcon sx={styles.arrowIcon} />
								) : (
									<ArrowRightIcon sx={styles.arrowIcon} />
								)}
							</IconButton>
						</div>
					</div>
				</div>
			</div>

			<div style={styles.zoomButtonsWrapper}>
				<div style={{ display: "flex" }}>
					<div className="ptz-zoom-buttons" style={styles.zoomIn}>
						<IconButton
							onMouseDown={() => handleMouseDown("zoomIn")}
							onMouseUp={() => handleMouseUp("zoomIn")}
						>
							<AddIcon className="ptz-zoom-button-content" />
						</IconButton>
					</div>
					<div className="ptz-zoom-buttons" style={styles.zoomOut}>
						<IconButton
							onMouseDown={() => handleMouseDown("zoomOut")}
							onMouseUp={() => handleMouseUp("zoomOut")}
						>
							<HorizontalRuleIcon className="ptz-zoom-button-content" />
						</IconButton>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PTZController;
